import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { errorLog } from "../../utils/logger";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { Modal } from "../../components/modals/Modal";
import { getAuth } from "firebase/auth";
import logout from "../../utils/logout";
import { useAtom } from "jotai";
import { userAtom, consentAcceptedAtomFamily } from "../../jotai/atoms";
import NotificationPreferencesCard from "./cards/NotificationPreferencesCard";
import ProfileInfoCard from "./cards/ProfileInfoCard";
import ContactPhoneCard from "./cards/ContactPhoneCard";
import DeleteAccountCard from "./cards/DeleteAccountCard";
import AboutCard from "./cards/AboutCard";

export function Settings() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState<string | boolean>(false);

  const [user] = useAtom(userAtom);
  const [_consentAccepted, setConsentAccepted] = useAtom(
    consentAcceptedAtomFamily(user?.email ?? "")
  );

  const getDeleteAccountModal = () => {
    return (
      <Modal
        id="delete-account-modal"
        open={showDeleteModal}
        hasClose={true}
        title={t("settings.delete_account")}
        onCloseModal={() => setShowDeleteModal(false)}
        actionButtons={
          <>
            <button
              className="btn btn-info"
              aria-label={t("generic.cancel")}
              onClick={() => setShowDeleteModal(false)}
            >
              {t("generic.cancel")}
            </button>
            <button
              disabled={deletingAccount}
              className="btn btn-primary"
              aria-label={t("generic.delete")}
              onClick={() => onDeleteUserAccount()}
            >
              {deletingAccount ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                t("generic.delete")
              )}
            </button>
          </>
        }
      >
        <p>{t("settings.delete_account_message")}</p>
      </Modal>
    );
  };

  const onDeleteUserAccount = async () => {
    try {
      setDeletingAccount(true);
      await getAPIClient().deleteUser();
      setConsentAccepted(false);
      await logout(getAPIClient(), getAuth());
      navigate("/");
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
      if (e instanceof Error) {
        errorLog(e);
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleError = (message: string) => {
    setShowErrorMessage(message);
  };

  return (
    <>
      {showDeleteModal && getDeleteAccountModal()}
      {showErrorMessage && (
        <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>{showErrorMessage}</ErrorModal>
      )}
      <div className="flex flex-col md:gap-4 md:mt-4">
        <ProfileInfoCard onError={handleError} />
        <ContactPhoneCard />
        <NotificationPreferencesCard onError={handleError} />
        <DeleteAccountCard onDeleteRequested={() => setShowDeleteModal(true)} />
        <AboutCard />
      </div>
    </>
  );
}

export default Settings;
